import { lazy, Suspense, useId, useRef, useState, type FormEvent } from "react";
import { flushSync } from "react-dom";
import { errorMessage, fieldErrors } from "../../shared/api/errors.ts";
import { DeviceOption } from "../../shared/devices/DeviceOption.tsx";
import { deviceName, isDeviceOnline, useActiveDevice, useDevices, type Device } from "../../shared/devices/devices.ts";
import { useAttachment } from "../../shared/media/attachment.ts";
import { BlobImage } from "../../shared/media/BlobImage.tsx";
import {
  displayTimes,
  MESSAGE_TEXT_MAX_LENGTH,
  textLength,
  useSendMessage,
  type DisplayTime,
  type ImageFit,
} from "../../shared/messages/messages.ts";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import { DeviceScreen } from "../../shared/device-screen/DeviceScreen.tsx";
import { BottomSheet, useCloseSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button, ButtonLink } from "../../shared/ui/Button/Button.tsx";
import { Spinner } from "../../shared/ui/Spinner/Spinner.tsx";
import { AttachmentField } from "./AttachmentField.tsx";
import styles from "./ComposeSheet.module.css";

/** Кнопки быстрых эмодзи из макета (Figma 315:1978); остальные — в шторке «Все эмодзи». */
const emojis = ["😍", "🤍", "🌸", "⭐"];
const EmojiPicker = lazy(() => import("./EmojiPicker.tsx"));

const SHOW_SENDER_KEY = "eva.compose.showSender";

/** Подписывать ли сообщение — последний выбор в этом браузере; по умолчанию подписывать. */
function readShowSender(): boolean {
  try {
    return localStorage.getItem(SHOW_SENDER_KEY) !== "false";
  } catch {
    return true;
  }
}

function saveShowSender(value: boolean) {
  try {
    localStorage.setItem(SHOW_SENDER_KEY, String(value));
  } catch {
    /* Без хранилища выбор действует до закрытия формы. */
  }
}

const TEXT_TOO_LONG = `Не больше ${MESSAGE_TEXT_MAX_LENGTH} символов`;
const TEXT_EMPTY = "Напишите сообщение";

/** Сообщение поверх главной; закрытие возвращает в ленту. */
export function ComposeSheet() {
  const close = useCloseSheet("/feed");
  return (
    <BottomSheet open onClose={close} onBack={close} title="Сообщение">
      <ComposeContent onSent={close} />
    </BottomSheet>
  );
}

function ComposeContent({ onSent }: { onSent: () => void }) {
  const devices = useDevices();
  const { active } = useActiveDevice();

  if (devices.isError) {
    return (
      <div className={styles.placeholder}>
        <p className={styles.notice}>{errorMessage(devices.error)}</p>
        <Button variant="secondary" onClick={() => void devices.refetch()}>
          Повторить
        </Button>
      </div>
    );
  }
  if (!devices.data) {
    return (
      <div className={styles.placeholder} role="status" aria-label="Загрузка">
        <Spinner size={48} />
      </div>
    );
  }
  if (!active) {
    return (
      <div className={styles.placeholder}>
        <p className={styles.notice}>Подключите E-va, чтобы отправлять сообщения</p>
        <ButtonLink to="/claim/scan" className={styles.wide}>
          Подключить E-va
        </ButtonLink>
      </div>
    );
  }
  return (
    <ComposeForm
      devices={devices.data}
      initialDeviceId={active.id}
      onSent={onSent}
    />
  );
}

type Errors = { text?: string; form?: string };

function ComposeForm({
  devices,
  initialDeviceId,
  onSent,
}: {
  devices: Device[];
  initialDeviceId: string;
  onSent: () => void;
}) {
  const [deviceId, setDeviceId] = useState(initialDeviceId);
  const [text, setText] = useState("");
  const [displayTime, setDisplayTime] = useState<DisplayTime>("2m");
  const [imageFit, setImageFit] = useState<ImageFit>("contain");
  const [showSender, setShowSender] = useState(readShowSender);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const { user } = useSession();
  // Курсор поля, пока фокус в шторке эмодзи: вставка идёт туда, где пользователь остановился.
  const selection = useRef({ start: 0, end: 0 });
  const send = useSendMessage();
  const attachment = useAttachment();
  const field = useRef<HTMLTextAreaElement>(null);
  const ids = { recipients: useId(), text: useId(), counter: useId(), error: useId(), duration: useId() };

  // Выбранная E-va могла пропасть из списка (например, владелец отозвал доступ): тогда — первая доступная.
  const device = devices.find((item) => item.id === deviceId) ?? devices[0]!;
  const length = textLength(text);
  const textError = length > MESSAGE_TEXT_MAX_LENGTH ? TEXT_TOO_LONG : errors.text;
  const { state: upload } = attachment;
  // Сообщение с вложением создаётся, только когда backend подготовил его для E-va.
  const media = upload.status === "ready" ? { assetId: upload.assetId, kind: upload.attachment.kind } : undefined;
  const uploadPending = upload.status !== "empty" && upload.status !== "ready";
  const previewImage = "attachment" in upload ? (upload.attachment?.blob ?? null) : null;
  const fields = { deviceId: device.id, text, displayTime, imageFit, showSender };

  function changeText(value: string) {
    setText(value);
    setErrors({});
  }

  function rememberSelection() {
    const input = field.current;
    if (input) selection.current = { start: input.selectionStart, end: input.selectionEnd };
  }

  /**
   * Эмодзи встаёт на место курсора или выделения, курсор — сразу за ним, клавиатура не закрывается.
   * Из шторки «Все эмодзи» поле не получает фокус, иначе телефон поднимет клавиатуру поверх шторки.
   */
  function insertEmoji(emoji: string, focus = true) {
    const input = field.current;
    const focused = input !== null && document.activeElement === input;
    const start = Math.min(focused ? input.selectionStart : selection.current.start, text.length);
    const end = Math.min(focused ? input.selectionEnd : selection.current.end, text.length);
    const next = text.slice(0, start) + emoji + text.slice(end);
    // Поле контролируемое: курсор ставится после того, как React запишет новое значение.
    flushSync(() => changeText(next));
    selection.current = { start: start + emoji.length, end: start + emoji.length };
    if (!focus) return;
    input?.focus();
    input?.setSelectionRange(start + emoji.length, start + emoji.length);
  }

  function changeShowSender(value: boolean) {
    setShowSender(value);
    saveShowSender(value);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (uploadPending) return;
    // Текст уходит как набран: счётчик и лимит считают ровно то, что получит backend. С вложением текст — подпись.
    if (!media && !text.trim()) {
      setErrors({ text: TEXT_EMPTY });
      return;
    }
    if (length > MESSAGE_TEXT_MAX_LENGTH) return;
    send.mutate(
      { ...fields, media },
      {
        onSuccess: () => {
          onSent();
        },
        onError: (error) => setErrors(errorsFrom(error)),
      },
    );
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <section className={styles.section}>
        <h3 id={ids.recipients} className={styles.label}>
          Кому отправить
        </h3>
        <div className={styles.recipients} role="group" aria-labelledby={ids.recipients}>
          {devices.map((item) => (
            <DeviceOption
              key={item.id}
              device={item}
              aria-pressed={item.id === device.id}
              onClick={() => setDeviceId(item.id)}
            />
          ))}
        </div>
        {!isDeviceOnline(device) && (
          <p className={styles.hint}>E-va сейчас offline. Сообщение встанет в очередь и покажется, когда она подключится</p>
        )}
      </section>

      <section className={styles.section}>
        <label htmlFor={ids.text} className={styles.label}>
          Напишите несколько теплых слов
        </label>
        <div className={styles.textBox}>
          <textarea
            ref={field}
            id={ids.text}
            className={styles.textarea}
            name="text"
            rows={3}
            placeholder="Что вы хотите сказать сегодня?"
            value={text}
            onChange={(event) => { changeText(event.target.value); rememberSelection(); }}
            onSelect={rememberSelection}
            aria-invalid={textError ? true : undefined}
            aria-describedby={textError ? `${ids.counter} ${ids.error}` : ids.counter}
          />
          <span id={ids.counter} className={styles.counter}>
            {length}/{MESSAGE_TEXT_MAX_LENGTH}
          </span>
        </div>
        {textError && (
          <p id={ids.error} className={styles.error}>
            {textError}
          </p>
        )}
        <div className={styles.emojis}>
          {emojis.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={styles.emoji}
              aria-label={`Вставить ${emoji}`}
              // Без фокуса на кнопке поле не теряет курсор, а клавиатура телефона не прячется.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => insertEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
          <button type="button" className={styles.emoji} aria-label="Все эмодзи" onMouseDown={(event) => event.preventDefault()} onClick={() => setPickerOpen(true)}>
            <span aria-hidden="true">＋</span>
          </button>
        </div>
        <BottomSheet title="Эмодзи" open={pickerOpen} onClose={() => setPickerOpen(false)}>
          <Suspense fallback={<Spinner />}>{pickerOpen && <EmojiPicker onPick={(emoji) => insertEmoji(emoji, false)} />}</Suspense>
          <Button variant="secondary" onClick={() => setPickerOpen(false)}>Готово</Button>
        </BottomSheet>
      </section>

      <AttachmentField
        state={upload}
        onPick={(file) => {
          setErrors({});
          attachment.pick(file);
        }}
        onRetry={attachment.retry}
        onClear={attachment.clear}
      />

      <Preview device={device} text={text.trim()} image={previewImage} imageFit={imageFit} onImageFit={setImageFit} sender={showSender ? user?.displayName : null} />

      <section className={styles.section}>
        <label className={styles.signRow}>
          <span>
            Подписать сообщение
            <small>{!showSender ? "Придёт без имени" : user?.displayName ? `На открытке будет видно «${user.displayName}»` : "Имя не указано в профиле"}</small>
          </span>
          <input type="checkbox" role="switch" checked={showSender} onChange={(event) => changeShowSender(event.target.checked)} />
        </label>
      </section>


      <section className={styles.section}>
        <h3 id={ids.duration} className={styles.label}>
          Время показа
        </h3>
        <div className={styles.durations} role="group" aria-labelledby={ids.duration}>
          {(Object.keys(displayTimes) as DisplayTime[]).map((value) => (
            <button
              key={value}
              type="button"
              className={styles.duration}
              aria-pressed={value === displayTime}
              onClick={() => setDisplayTime(value)}
            >
              {displayTimes[value].label}
            </button>
          ))}
        </div>
        <p className={styles.hint}>{displayTime === "forever" ? "Следующие сообщения будут ждать, пока вы закроете это на открытке." : "Отсчёт начнётся, когда сообщение появится на экране."}</p>
      </section>


      <div className={styles.actions}>
        {errors.form && (
          <p className={styles.formError} role="alert">
            {errors.form}
          </p>
        )}
        <Button type="submit" loading={send.isPending} disabled={uploadPending}>
          Отправить
        </Button>
      </div>

    </form>
  );
}

/**
 * «Превью на E-va» (Figma 317:2024): экран 320×240, как у E-va в альбомной ориентации. Прошивка подбирает
 * шрифт под длину текста; превью делает то же по ступеням, поэтому это ориентир, а не точная копия.
 * Фото занимает весь экран, подпись — на затемнении снизу, имя отправителя — плашкой слева сверху.
 */
function Preview({ device, text, image, imageFit, onImageFit, sender }: { device: Device; text: string; image: Blob | null; imageFit: ImageFit; onImageFit: (fit: ImageFit) => void; sender?: string | null }) {
  const titleId = useId();
  return (
    <section className={styles.previewSection} aria-labelledby={titleId}>
      <div className={styles.previewHeading}><h3 id={titleId} className={styles.label}>Превью на {deviceName(device)}</h3><span>Экран открытки</span></div>
      <div className={styles.postcard}><DeviceScreen text={text} image={image} imageFit={imageFit} sender={sender} /></div>
      {image && <div className={styles.fitOptions} role="group" aria-label="Режим фото">
        {([['contain', 'Целиком', 'Без обрезки, с полями'], ['cover', 'Заполнить экран', 'Края будут обрезаны']] as const).map(([value, label, description]) => <button className={styles.fitOption} key={value} type="button" aria-label={label} aria-pressed={imageFit === value} onClick={() => onImageFit(value)}><div className={styles.fitThumb}><BlobImage blob={image} alt="" aria-hidden="true" style={{ objectFit: value }} /></div><span className={styles.fitLabel}>{label}<small>{description}</small></span><span className={styles.fitRadio} aria-hidden="true" /></button>)}
      </div>}
      {image && text && <p className={styles.note}>Подпись ляжет поверх фото внизу экрана E-va</p>}
      <p className={styles.note}>{image ? "Кадрирование, подпись и переносы показаны в формате экрана открытки. Подпись — до двух строк." : "Превью повторяет шрифты, переносы и отступы экрана E-va."}</p>
    </section>
  );
}

function errorsFrom(error: Error): Errors {
  const byField = fieldErrors(error, ["text"] as const);
  return byField.text ? byField : { form: errorMessage(error) };
}
